/*
 * Cloud 10 gift-with-purchase (GWP) cards  (Oct 2026)
 *
 * Reads the live BOGOS offers (window.fgData.offers) and shows:
 *   - product pages:  gift card(s) under Add to bag
 *   - bag drawer and bag page:  spend-gift progress panel, top-up suggestions,
 *                               brand offer reminders under qualifying items
 *   - collection pages:  an offer tile in the product grid (page 1)
 *
 * Nothing here adds gifts: BOGOS still does that. This only displays the offers.
 * No timers run in the background: it re-renders only when the bag changes.
 */
(function () {
  'use strict';

  var CFG = window.C10GWP || {};
  var ROOT = CFG.root || '/';
  if (ROOT.slice(-1) !== '/') ROOT += '/';
  var CART_URL = CFG.cartUrl || '/cart';
  var CUR = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  var RATE = parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1;

  /* ---------- small helpers ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var fmtCache = {};
  // amount is in the shopper's currency (major units, e.g. 12.5)
  function money(amount, whole) {
    var key = whole ? 'w' : 'd';
    if (!fmtCache[key]) {
      try {
        fmtCache[key] = new Intl.NumberFormat('en-IE', {
          style: 'currency', currency: CUR,
          minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
        });
      } catch (e) {
        fmtCache[key] = { format: function (n) { return CUR + ' ' + n.toFixed(whole ? 0 : 2); } };
      }
    }
    // whole amounts are thresholds/values shown without pence: always round UP, never below the real figure
    return fmtCache[key].format(whole ? Math.ceil(amount - 1e-9) : amount);
  }
  // euro (shop currency) -> shopper's currency, rounded UP to the cent
  function toLocal(eur) { return Math.ceil(eur * RATE * 100 - 1e-6) / 100; }
  function handleize(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function plural(n, one, many) { return n === 1 ? one : many; }

  function store(key, val, minutes) {
    try { sessionStorage.setItem('c10gwp2:' + key, JSON.stringify({ t: Date.now() + minutes * 60000, v: val })); } catch (e) {}
  }
  function recall(key) {
    try {
      var raw = sessionStorage.getItem('c10gwp2:' + key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      return o.t > Date.now() ? o.v : null;
    } catch (e) { return null; }
  }

  function getJSON(url) {
    // no "Accept: application/json" header: with it, Shopify ignores ?view= and returns its own product JSON
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (t) { return JSON.parse(t); });
  }

  /* ---------- BOGOS offers ---------- */

  function waitForOffers() {
    return new Promise(function (resolve) {
      var tries = 0;
      (function check() {
        var d = window.fgData;
        if (d && Array.isArray(d.offers)) return resolve(d.offers);
        if (++tries > 60) return resolve([]); // give up after ~9s
        setTimeout(check, 150);
      })();
    });
  }

  function cleanGiftName(t) {
    return String(t || '')
      .replace(/^\s*»\s*/, '')
      .replace(/\s*\(\s*\d+\s*%\s*off\s*\)\s*$/i, '')
      .replace(/\bGWP\b/gi, '')
      .replace(/^\s*free\s+/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function giftLabel(names) {
    if (names.length <= 1) return names[0] || 'gift';
    if (names.length === 2) return names[0] + ' & ' + names[1];
    return names.length + ' gifts';
  }

  // Turn a raw BOGOS offer into what the cards need. Returns null for anything
  // these cards don't understand (multi-rule offers etc.), so we never show a wrong claim.
  function parseOffer(o) {
    if (!o || o.deleted_at) return null;
    var now = Date.now();
    if (o.start_time && Date.parse(o.start_time) > now) return null;
    if (o.end_time && Date.parse(o.end_time) <= now) return null;
    var conds = o.offer_conditions || [];
    if (conds.length !== 1) return null;
    // offers with extra conditions (customer tags, birthdays, markets...) are left to BOGOS
    var sub = o.sub_conditions;
    if (sub && (Array.isArray(sub) ? sub.length : Object.keys(sub).length)) return null;
    var c = conds[0];
    var unit = c.unit === 'money' ? 'money' : (c.unit === 'number_of_products' ? 'count' : null);
    if (!unit) return null;
    var min = parseFloat(c.min_amount);
    if (!(min > 0)) return null;

    var narrow = c.product_narrow || [];
    if (narrow.length > 1) return null;
    var rule = { kind: 'all' };
    if (narrow.length === 1) {
      var n = narrow[0];
      if (n.filter_option && n.filter_option !== 'include') return null;
      var val = n.value || {};
      if (n.type === 'product_vendors') {
        var names = Object.keys(val).map(function (k) { return val[k] || k; });
        rule = { kind: 'vendor', vendors: names.map(function (s) { return String(s).toLowerCase(); }), name: names[0] };
        rule.url = ROOT + 'collections/' + handleize(names[0]);
      } else if (n.type === 'product_collections') {
        var ids = Object.keys(val);
        var handles = n.handle || {};
        rule = { kind: 'collection', ids: ids.map(String), name: val[ids[0]], handle: handles[ids[0]], handles: handles };
        rule.url = rule.handle ? ROOT + 'collections/' + rule.handle : null;
      } else if (n.type === 'variants_ids' || n.type === 'products_ids' || n.type === 'product_ids') {
        var pids = Object.keys(val);
        var first = val[pids[0]] || {};
        var vids = [];
        pids.forEach(function (k) { ((val[k] || {}).variants || []).forEach(function (x) { if (x && x.id) vids.push(String(x.id)); }); });
        rule = { kind: 'products', ids: pids.map(String), variantIds: vids, name: first.vendor || first.title, title: first.title, vendor: first.vendor };
        rule.url = first.handle ? ROOT + 'products/' + first.handle : null;
      } else {
        return null;
      }
    }

    var gifts = (o.gift_products || []).map(function (g) {
      var v = (g.variants || [])[0] || {};
      return {
        name: cleanGiftName(g.title),
        handle: g.original_product_handle,
        image: g.thumbnail || v.thumbnail || null,
        price: parseFloat(v.original_price) || 0,
        variantIds: (g.variants || []).map(function (x) { return String(x.original_variant_shopify_id); })
      };
    });
    if (!gifts.length) return null;

    var promo = o.promotion_message ? String(o.promotion_message).replace(/<[^>]*>/g, '').trim() : '';
    // "Offer title" in the BOGOS admin (stored by BOGOS as offer_name); falls back to the internal offer name
    var display = String(o.offer_name || o.title || '').replace(/<[^>]*>/g, '').replace(/\s{2,}/g, ' ').trim();

    return {
      id: o.id,
      unit: unit,
      min: min,
      rule: rule,
      gifts: gifts,
      giftName: giftLabel(gifts.map(function (g) { return g.name; })),
      promo: promo,
      display: display,
      priority: o.priority || 0,
      auto: !!(o.gift_settings && o.gift_settings.auto_add_all_gifts),
      isSpend: rule.kind === 'all' && unit === 'money'
    };
  }

  function matches(offer, p) {
    var r = offer.rule;
    if (r.kind === 'all') return true;
    if (r.kind === 'vendor') return r.vendors.indexOf(String(p.vendor || '').toLowerCase()) !== -1;
    if (r.kind === 'products') {
      if (r.ids.indexOf(String(p.id)) === -1) return false;
      // offer limited to certain sizes: check the size too
      if (r.variantIds && r.variantIds.length && p.variantId) return r.variantIds.indexOf(String(p.variantId)) !== -1;
      return true;
    }
    if (r.kind === 'collection') {
      var cols = (p.collections || []).map(String);
      for (var i = 0; i < r.ids.length; i++) if (cols.indexOf(r.ids[i]) !== -1) return true;
      return false;
    }
    return false;
  }

  /* ---------- wording ---------- */

  function offerName(o) { return o.rule.name || ''; }

  // The offer's own wording from BOGOS: promotion message first, then the offer title.
  // A name with a € amount is only used for shoppers paying in euro (it would be wrong in £ or $).
  function ownWording(o) {
    if (o.promo) return o.promo;
    if (o.display && (CUR === 'EUR' || !/[€£$]/.test(o.display))) return o.display;
    return '';
  }

  function headline(o) {
    var own = ownWording(o);
    if (own) return own;
    var gift = 'a FREE ' + o.giftName;
    var name = offerName(o);
    if (o.unit === 'money') return 'Spend ' + money(toLocal(o.min), true) + ' on ' + name + ', get ' + gift;
    if (o.rule.kind === 'products') {
      return o.rule.ids.length === 1 && o.min === 1
        ? 'Buy ' + (o.rule.title || 'this product') + ', get ' + gift
        : 'Buy any ' + o.min + ' selected ' + name + ' ' + plural(o.min, 'product', 'products') + ', get ' + gift;
    }
    return o.min === 1
      ? 'Buy any ' + name + ' product, get ' + gift
      : 'Buy any ' + o.min + ' ' + name + ' products, get ' + gift;
  }

  /* ---------- the bag ---------- */

  var giftVariantIds = {};
  var excludedTypes = { FREEGIFT_HIDDEN: 1, Sample: 1, 'Gift Card': 1 };

  function isGiftLine(item) {
    if (excludedTypes[item.product_type]) return true;
    if (item.gift_card) return true;
    if (/sca_clone_freegift/.test(item.handle || '')) return true;
    if (giftVariantIds[String(item.variant_id)]) return true;
    if (item.final_line_price === 0 && item.original_line_price > 0) return true;
    return false;
  }

  // Lines that count towards offers, in euro. Like BOGOS in the bag, this uses what they'll pay:
  // sale prices AFTER any discount code already in the bag (item codes and whole-bag codes,
  // the whole-bag ones shared across the lines). BOGOS's own gift discounts are ignored.
  function countedLines(cart) {
    var items = (cart.items || []).filter(function (i) { return !isGiftLine(i); });
    var paid = function (i) { return i.final_line_price != null ? i.final_line_price : i.original_line_price; };
    var sub = items.reduce(function (s, i) { return s + paid(i); }, 0);
    var bagOff = (cart.cart_level_discount_applications || []).reduce(function (s, a) {
      return /bogos|free\s*gift/i.test(String(a.title || a.key || '')) ? s : s + (a.total_allocated_amount || 0);
    }, 0);
    var factor = sub > 0 ? Math.max(0, sub - bagOff) / sub : 1;
    return items.map(function (i) {
      return {
        key: i.key, index: cart.items.indexOf(i), id: i.product_id, variantId: i.variant_id, handle: i.handle,
        vendor: i.vendor, qty: i.quantity, eur: paid(i) * factor / 100 / RATE, collections: null
      };
    });
  }

  function cartTotalEur(lines) {
    return lines.reduce(function (s, l) { return s + l.eur; }, 0);
  }

  // progress of one offer against the bag (+ an optional extra product, for product pages)
  function progress(o, lines, extra) {
    var have = 0;
    lines.forEach(function (l) {
      if (matches(o, l)) have += o.unit === 'money' ? l.eur : l.qty;
    });
    var withExtra = have;
    if (extra && matches(o, extra)) withExtra += o.unit === 'money' ? extra.eur : extra.qty;
    return { have: have, withExtra: withExtra, remaining: Math.max(0, o.min - have), remainingAfter: Math.max(0, o.min - withExtra), done: have >= o.min - 1e-9 };
  }

  // collections are only needed when a live offer is collection-based
  function productInfo(handle) {
    var cached = recall('p:' + handle);
    if (cached) return Promise.resolve(cached);
    return getJSON(ROOT + 'products/' + encodeURIComponent(handle) + '?view=c10-gwp')
      .then(function (d) {
        if (!d || d.product) return null; // Shopify's own product JSON, not our data view
        store('p:' + handle, d, 10); return d;
      })
      .catch(function () { return null; });
  }

  // Sold-out gifts: drop them from an offer; if none are left, the offer isn't shown at all.
  // Only the offers about to be shown on this page are checked (cached for 10 minutes).
  function checkStock(offers) {
    return Promise.all(offers.filter(function (o) { return !o.stockChecked; }).map(function (o) {
      return Promise.all(o.gifts.map(function (g) {
        if (!g.handle) return null;
        return productInfo(g.handle).then(function (d) { if (d && d.available === false) g.soldOut = true; });
      })).then(function () {
        o.stockChecked = true;
        var live = o.gifts.filter(function (g) { return !g.soldOut; });
        if (!live.length) o.soldOut = true;
        else if (live.length !== o.gifts.length) {
          o.gifts = live;
          o.giftName = giftLabel(live.map(function (g) { return g.name; }));
        }
      });
    }));
  }

  function live(list) { return list.filter(function (o) { return !o.soldOut; }); }

  function tileMatches(o) {
    var C = CFG.collection;
    if (!C || o.isSpend) return false;
    if (o.rule.kind === 'vendor') return o.rule.vendors.some(function (v) { return handleize(v) === C.handle; });
    if (o.rule.kind === 'collection') return o.rule.ids.indexOf(String(C.id)) !== -1;
    return false;
  }

  function addCollections(lines, offers) {
    var need = offers.some(function (o) { return o.rule.kind === 'collection'; });
    if (!need) return Promise.resolve(lines);
    return Promise.all(lines.map(function (l) {
      return productInfo(l.handle).then(function (d) { l.collections = d ? d.collections : []; });
    })).then(function () { return lines; });
  }

  function topUps() {
    var cached = recall('topups:' + CUR);
    if (cached) return Promise.resolve(cached);
    return getJSON(ROOT + 'collections/' + encodeURIComponent(CFG.topupHandle || 'top-up-picks') + '?view=c10-gwp')
      .then(function (d) { d = Array.isArray(d) ? d : []; store('topups:' + CUR, d, 10); return d; })
      .catch(function () { return []; });
  }

  // pick suggestions that close the gap: first the cheapest that gets them there, then the nearest below
  function pickTopUps(list, remainingLocal, inBagIds, n) {
    var pool = list.filter(function (p) { return p.available && inBagIds.indexOf(p.id) === -1; });
    var over = pool.filter(function (p) { return p.price / 100 >= remainingLocal; }).sort(function (a, b) { return a.price - b.price; });
    var under = pool.filter(function (p) { return p.price / 100 < remainingLocal; }).sort(function (a, b) { return b.price - a.price; });
    return over.concat(under).slice(0, n);
  }

  /* ---------- markup ---------- */

  function bar(pct, label, cls) {
    pct = Math.max(0, Math.min(100, pct));
    return '<div class="c10g-bar ' + (cls || '') + '" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' +
      Math.round(pct) + '" aria-label="' + esc(label) + '"><span style="width:' + pct.toFixed(1) + '%"></span></div>';
  }

  function img(src, alt, w) {
    if (!src) return '<span class="c10g-img-empty" aria-hidden="true"></span>';
    var s = String(src);
    if (w && /cdn\.shopify\.com|\/cdn\/shop\//.test(s) && s.indexOf('width=') === -1) s += (s.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    return '<img src="' + esc(s) + '" alt="' + esc(alt || '') + '" loading="lazy" width="' + (w || 300) + '" height="' + (w || 300) + '">';
  }

  function worthText(spendGift) {
    return spendGift && spendGift.worth ? 'worth ' + money(spendGift.worth, true) : '';
  }

  // spend gift: big card with gallery (product pages without a brand offer)
  function spendBig(o, g, line) {
    var images = (g.images && g.images.length ? g.images : [o.gifts[0].image]).filter(Boolean);
    var slides = images.map(function (src, i) {
      return '<div class="c10g-slide' + (i === 0 ? ' is-active' : '') + '">' + img(src, o.giftName + ' image ' + (i + 1), 640) + '</div>';
    }).join('');
    var nav = images.length > 1
      ? '<button type="button" class="c10g-nav c10g-prev" aria-label="Previous gift image">' + chev('l') + '</button>' +
        '<button type="button" class="c10g-nav c10g-next" aria-label="Next gift image">' + chev('r') + '</button>' +
        '<div class="c10g-dots">' + images.map(function (_, i) { return '<span' + (i === 0 ? ' class="is-active"' : '') + '></span>'; }).join('') + '</div>'
      : '';
    var contents = (g.contents || []).slice(0, 10);
    var inside = contents.length
      ? '<div class="c10g-inside"><p class="c10g-eyebrow">What\'s inside</p><ul>' + contents.map(function (c) {
          return '<li>' + img(c.image, c.title, 200) + '<span>' + esc(c.title) + '</span></li>';
        }).join('') + '</ul></div>'
      : '';
    var w = worthText(g);
    return '<div class="c10g-card c10g-spend c10g-big">' +
      '<div class="c10g-gallery" data-c10g-gallery>' + slides +
        (w ? '<span class="c10g-pill">FREE GIFT · ' + esc(w.toUpperCase()) + '</span>' : '<span class="c10g-pill">FREE GIFT</span>') + nav + '</div>' +
      inside +
      '<div class="c10g-body">' +
        '<p class="c10g-title">' + esc(ownWording(o) || ('Spend ' + money(toLocal(o.min), true) + ' and get this gift FREE')) + '</p>' +
        line +
      '</div></div>';
  }

  // compact card: image edge to edge on the left (brand offers and the spend gift next to them)
  function compact(kind, label, title, image, alt, bodyHtml, pill) {
    return '<div class="c10g-card c10g-compact c10g-' + kind + '">' +
      '<div class="c10g-thumb">' + img(image, alt, 340) + (pill ? '<span class="c10g-pill c10g-pill-sm">' + esc(pill) + '</span>' : '') + '</div>' +
      '<div class="c10g-body"><p class="c10g-eyebrow">' + esc(label) + '</p><p class="c10g-title">' + esc(title) + '</p>' + bodyHtml + '</div></div>';
  }

  function chev(dir) {
    return '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="' +
      (dir === 'l' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5') + '" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  }

  function wireGalleries(root) {
    root.querySelectorAll('[data-c10g-gallery]').forEach(function (gal) {
      if (gal.__c10g) return; gal.__c10g = true;
      var slides = gal.querySelectorAll('.c10g-slide'), dots = gal.querySelectorAll('.c10g-dots span'), i = 0;
      function go(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
      }
      var p = gal.querySelector('.c10g-prev'), n = gal.querySelector('.c10g-next');
      if (p) p.addEventListener('click', function () { go(i - 1); });
      if (n) n.addEventListener('click', function () { go(i + 1); });
    });
  }

  /* ---------- state ---------- */

  var OFFERS = [];      // parsed, live
  var SPEND = null;     // the spend-threshold offer (Cloud 10 Beauty Gift)
  var SPEND_GIFT = null; // {worth, images, contents} from the gift product
  var CART = null;

  function spendGiftInfo() {
    if (!SPEND || !SPEND.gifts[0].handle) return Promise.resolve(null);
    return productInfo(SPEND.gifts[0].handle).then(function (d) {
      var g = SPEND.gifts[0];
      SPEND_GIFT = {
        worth: d && d.price ? d.price / 100 : toLocal(g.price),
        images: d && d.images && d.images.length ? d.images : (g.image ? [g.image] : []),
        contents: (d && d.contents) || []
      };
      return SPEND_GIFT;
    });
  }

  function fetchCart() {
    return getJSON(ROOT + 'cart.js').then(function (c) { CART = c; return c; }).catch(function () { return CART || { items: [] }; });
  }

  /* ---------- product page ---------- */

  function renderProduct(lines) {
    var P = CFG.product;
    if (!P) return;
    var anchor = document.querySelector('[id^="MainProduct-"] .buy_with_qty');
    if (!anchor) return;
    var box = document.getElementById('c10g-pdp');
    if (!box) {
      box = document.createElement('div');
      box.id = 'c10g-pdp';
      box.className = 'c10g';
      anchor.insertAdjacentElement('afterend', box);
    }

    var form = document.querySelector('[id^="MainProduct-"] form[action*="/cart/add"]');
    var vid = (form && form.querySelector('input[name="id"]') && form.querySelector('input[name="id"]').value) || P.variant;
    var qtyInput = document.querySelector('[id^="MainProduct-"] .quantity__input');
    var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    var priceLocal = ((P.prices && P.prices[vid]) || 0) / 100 * qty;
    var extra = { id: P.id, variantId: vid, vendor: P.vendor, collections: P.collections, qty: qty, eur: priceLocal / RATE };

    var brand = OFFERS.filter(function (o) { return !o.isSpend && matches(o, extra); });
    var html = '';

    brand.forEach(function (o) {
      var pr = progress(o, lines, extra);
      var line;
      if (pr.done) line = '<p class="c10g-msg">You\'ve unlocked this gift.</p>';
      else if (o.unit === 'money') {
        line = pr.remainingAfter <= 0
          ? '<p class="c10g-msg">Add this to your bag to claim it.</p>'
          : '<p class="c10g-msg">Add this and spend ' + money(toLocal(pr.remainingAfter)) + ' more on ' + esc(offerName(o)) + ' to claim it.</p>';
        line += bar(pr.withExtra / o.min * 100, offerName(o) + ' spend towards gift', 'c10g-bar-dark');
      } else {
        var left = Math.ceil(pr.remainingAfter);
        line = left <= 0
          ? '<p class="c10g-msg">Add this to your bag to claim it.</p>'
          : '<p class="c10g-msg">Add this and ' + left + ' more ' + esc(offerName(o)) + ' ' + plural(left, 'product', 'products') + ' to claim it.</p>';
      }
      if (o.rule.url && !pr.done && o.rule.kind !== 'products') line += '<a class="c10g-link" href="' + esc(o.rule.url) + '">Shop ' + esc(offerName(o)) + '</a>';
      html += compact('brand', (offerName(o) || 'Brand') + ' offer', headline(o), o.gifts[0].image, o.giftName, line);
    });

    if (SPEND) {
      var sp = progress(SPEND, lines, extra);
      var sline;
      if (sp.done) sline = '<p class="c10g-msg">You\'ve unlocked your FREE gift.</p>';
      else if (sp.remainingAfter <= 0) sline = '<p class="c10g-msg">Add this to unlock your FREE gift.</p>';
      else sline = '<p class="c10g-msg">Add this and you\'re just <b>' + money(toLocal(sp.remainingAfter)) + '</b> away.</p>';
      sline += bar(sp.withExtra / SPEND.min * 100, 'Bag progress towards free gift', '');
      sline += '<button type="button" class="c10g-link" data-c10g-details>What\'s Inside</button>';

      // always the compact strip, with or without brand offers (the large box was too big)
      var w = worthText(SPEND_GIFT);
      var title = ownWording(SPEND) || ('Spend ' + money(toLocal(SPEND.min), true) + ', get a FREE gift' + (w ? ' ' + w : ''));
      html += compact('spend', CFG.spendLabel || 'Free gift', title, SPEND_GIFT && SPEND_GIFT.images[0] || SPEND.gifts[0].image, SPEND.giftName, sline, w ? w.toUpperCase() : '');
    }

    box.innerHTML = html;
    document.body.classList.toggle('c10g-pdp-on', !!html);
    wireGalleries(box);
    var det = box.querySelector('[data-c10g-details]');
    if (det) det.addEventListener('click', function () { openDetails(); });
  }

  // "What's Inside" pop-up for the spend gift: the gift photos plus the "What's inside?" list
  // read from the free gift page (so it only ever needs updating there)
  var GIFT_PAGE = CFG.giftPageHandle || 'cloud-10-beauty-free-gift';

  function giftPageInfo() {
    var cached = recall('page:' + GIFT_PAGE);
    if (cached) return Promise.resolve(cached);
    return getJSON(ROOT + 'pages/' + encodeURIComponent(GIFT_PAGE) + '?view=c10-gwp').then(function (d) {
      var html = d.content || (d.page && d.page.body_html) || '';
      var doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
      var items = [];
      var heads = doc.querySelectorAll('p, h1, h2, h3, h4, h5, h6, strong, div');
      for (var i = 0; i < heads.length && !items.length; i++) {
        if (!/what.{0,2}s\s+inside/i.test(heads[i].textContent || '')) continue;
        var el = heads[i];
        while (el && el.tagName !== 'P' && /^(STRONG|B|SPAN|EM)$/.test(el.tagName)) el = el.parentElement;
        var n = el && el.nextElementSibling;
        while (n && !/^(UL|OL)$/.test(n.tagName)) n = n.nextElementSibling;
        if (n) n.querySelectorAll('li').forEach(function (li) { var t = li.textContent.trim(); if (t) items.push(t); });
      }
      var info = { items: items, url: d.url || (ROOT + 'pages/' + GIFT_PAGE) };
      if (items.length) store('page:' + GIFT_PAGE, info, 10);
      return info;
    }).catch(function () { return { items: [], url: ROOT + 'pages/' + GIFT_PAGE }; });
  }

  function openDetails() {
    if (!SPEND) return;
    var g = SPEND_GIFT || { images: [], contents: [] };
    var d = document.getElementById('c10g-dialog');
    if (!d) { d = document.createElement('dialog'); d.id = 'c10g-dialog'; d.className = 'c10g c10g-dialog'; document.body.appendChild(d); }
    var w = worthText(g);
    var images = (g.images || []).slice(0, 4);
    d.innerHTML =
      '<div class="c10g-dialog-head"><p class="c10g-eyebrow">' + esc(CFG.spendLabel || 'Free gift') + (w ? ' · ' + esc(w) : '') + '</p>' +
      '<button type="button" class="c10g-close" aria-label="Close">×</button></div>' +
      '<p class="c10g-title">What\'s Inside</p>' +
      '<p class="c10g-msg" style="margin-top:0.4rem">Spend ' + esc(money(toLocal(SPEND.min), true)) + ' and this gift is added to your bag automatically. One per order, while stocks last.</p>' +
      (images.length ? '<div class="c10g-dialog-imgs' + (images.length === 1 ? ' is-one' : '') + '">' + images.map(function (s) { return img(s, SPEND.giftName, 640); }).join('') + '</div>' : '') +
      '<div data-c10g-inside><p class="c10g-small">Loading…</p></div>';
    d.querySelector('.c10g-close').addEventListener('click', function () { d.close(); });
    if (!d.__c10g) { d.__c10g = true; d.addEventListener('click', function (e) { if (e.target === d) d.close(); }); }
    if (d.showModal) d.showModal(); else d.setAttribute('open', '');
    giftPageInfo().then(function (info) {
      var box = d.querySelector('[data-c10g-inside]');
      if (!box) return;
      var list = info.items.length ? info.items : (g.contents || []).map(function (c) { return c.title; });
      box.innerHTML = list.length
        ? '<ul class="c10g-dialog-list">' + list.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>'
        : '';
    });
  }

  /* ---------- bag drawer + bag page ---------- */

  function bagPanel(lines, context, suggestions) {
    if (!SPEND) return '';
    var pr = progress(SPEND, lines, null);
    var w = worthText(SPEND_GIFT);
    var label = CFG.spendLabel || 'Free gift';
    var image = (SPEND_GIFT && SPEND_GIFT.images[0]) || SPEND.gifts[0].image;
    var title, sub;
    if (pr.done) {
      title = 'You\'ve unlocked your FREE ' + label + (w ? ' ' + w : '');
      sub = 'It\'s in your bag below.';
    } else {
      title = 'You\'re ' + money(toLocal(pr.remaining)) + ' away from your FREE ' + label + (w ? ' ' + w : '');
      sub = 'Spend ' + money(toLocal(SPEND.min), true) + ' to unlock it.';
    }
    var top = '';
    if (!pr.done && suggestions && suggestions.length) {
      top = '<div class="c10g-topups"><p class="c10g-eyebrow">' + (toLocal(pr.remaining) <= 25 ? 'Add one to unlock your gift' : 'Top up to unlock') + '</p><ul>' +
        suggestions.map(function (p) {
          return '<li><a href="' + esc(p.url) + '" class="c10g-tu-img">' + img(p.image, p.title, 240) + '</a>' +
            '<a href="' + esc(p.url) + '" class="c10g-tu-name">' + esc(p.title) + '</a>' +
            '<span class="c10g-tu-price">' + money(p.price / 100) + '</span>' +
            '<button type="button" class="c10g-add" data-c10g-add="' + p.variant + '">Add to bag</button></li>';
        }).join('') + '</ul></div>';
    }
    return '<div class="c10g-card c10g-panel c10g-panel-' + context + '">' +
      '<div class="c10g-panel-top"><div class="c10g-thumb">' + img(image, SPEND.giftName, 300) +
        (w ? '<span class="c10g-pill c10g-pill-sm">' + esc(w.toUpperCase()) + '</span>' : '') + '</div>' +
      '<div class="c10g-body"><p class="c10g-eyebrow">' + esc(label) + '</p><p class="c10g-title">' + esc(title) + '</p>' +
        '<p class="c10g-msg">' + esc(sub) + '</p>' + bar(pr.have / SPEND.min * 100, 'Bag progress towards free gift', '') + '</div></div>' +
      top + '</div>';
  }

  // brand reminder under the first qualifying line of each brand offer: a small faded picture of the
  // gift labelled "NOT YET", so it reads as a next step rather than something already in the bag
  function giftShort(o) {
    if (!ownWording(o)) return 'FREE ' + o.giftName;
    var t = headline(o).replace(/\s+(with|when you buy|when you spend)\s+(any|a|an|every|\d|€|£|\$).*$/i, '').trim();
    return t || ('FREE ' + o.giftName);
  }

  function lineReminders(lines) {
    var out = {}; // line index -> html
    OFFERS.forEach(function (o) {
      if (o.isSpend) return;
      var first = null;
      lines.forEach(function (l) { if (!first && matches(o, l)) first = l; });
      if (!first) return;
      var pr = progress(o, lines, null);
      var name = offerName(o);
      var title, sub = esc(giftShort(o));
      if (pr.done) {
        title = 'Unlocked · it\'s in your bag';
      } else if (o.unit === 'money') {
        title = 'Spend ' + money(toLocal(pr.remaining)) + ' more on ' + name + ' to unlock';
      } else {
        var left = Math.ceil(pr.remaining);
        title = o.rule.kind === 'products'
          ? 'Add ' + left + ' more to unlock'
          : 'Add ' + left + ' more ' + name + ' ' + plural(left, 'product', 'products') + ' to unlock';
      }
      if (!pr.done && o.rule.url && o.rule.kind !== 'products') sub += ' · <a href="' + esc(o.rule.url) + '">Shop ' + esc(name) + '</a>';
      out[first.index] = (out[first.index] || '') +
        '<div class="c10g-remind' + (pr.done ? ' is-done' : '') + '">' +
          '<span class="c10g-remind-img">' + img(o.gifts[0].image, o.giftName, 120) + '<b>' + (pr.done ? 'ADDED' : 'NOT YET') + '</b></span>' +
          '<div class="c10g-remind-txt"><p class="c10g-remind-t">' + esc(title) + '</p><p class="c10g-remind-s">' + sub + '</p></div>' +
        '</div>';
    });
    return out;
  }

  // the reminder goes on its own row under the item, lined up with the item's name and using the
  // full width (so the wording isn't squeezed next to the price)
  function placeLines(rowPrefix, reminders) {
    document.querySelectorAll('[data-c10g="' + rowPrefix + '"]').forEach(function (n) { n.remove(); });
    Object.keys(reminders).forEach(function (idx) {
      var row = document.getElementById(rowPrefix + (parseInt(idx, 10) + 1));
      if (!row) return;
      var html = '<div class="c10g c10g-line-wrap">' + reminders[idx] + '</div>';
      var cells = row.cells ? row.cells.length : 0;
      if (row.tagName === 'TR' && cells >= 2) {
        var tr = document.createElement('tr');
        tr.className = 'c10g-line-row';
        tr.setAttribute('data-c10g', rowPrefix);
        tr.innerHTML = '<td class="c10g-line-gap"></td><td colspan="' + (cells - 1) + '">' + html + '</td>';
        // the theme lays its rows out as flex/grid boxes rather than table rows: start the reminder
        // where the item's name starts
        if (getComputedStyle(row).display !== 'table-row') {
          var offset = row.cells[1].getBoundingClientRect().left - row.getBoundingClientRect().left;
          if (!(offset > 0)) offset = row.cells[0].getBoundingClientRect().width;
          tr.style.display = 'flex';
          tr.cells[0].style.flex = '0 0 ' + Math.round(offset) + 'px';
          tr.cells[1].style.flex = '1 1 auto';
          tr.cells[1].style.minWidth = '0';
        }
        row.insertAdjacentElement('afterend', tr);
      } else {
        var cell = row.querySelector('.cart-item__details') || row;
        var wrap = document.createElement('div');
        wrap.setAttribute('data-c10g', rowPrefix);
        wrap.innerHTML = html;
        cell.appendChild(wrap);
      }
    });
  }

  function renderBag(lines) {
    var inBag = lines.map(function (l) { return l.id; });
    var pr = SPEND ? progress(SPEND, lines, null) : null;
    var wantTopups = SPEND && !pr.done;
    return (wantTopups ? topUps() : Promise.resolve([])).then(function (list) {
      var remainingLocal = pr ? toLocal(pr.remaining) : 0;
      var reminders = lineReminders(lines);

      // drawer
      var inner = document.querySelector('#CartDrawer .drawer__inner');
      if (inner && !document.querySelector('cart-drawer.is-empty')) {
        var after = inner.querySelector('.cart__drawer__btns');
        var panel = inner.querySelector('#c10g-drawer');
        if (!panel) { panel = document.createElement('div'); panel.id = 'c10g-drawer'; panel.className = 'c10g'; }
        if (after && panel.previousElementSibling !== after) after.insertAdjacentElement('afterend', panel);
        panel.innerHTML = lines.length ? bagPanel(lines, 'drawer', pickTopUps(list, remainingLocal, inBag, 3)) : '';
        placeLines('CartDrawer-Item-', reminders);
      }

      // bag page
      if (document.body.classList.contains('template-cart')) {
        var bar0 = document.getElementById('cart-shipping-bar');
        var pp = document.getElementById('c10g-cart');
        if (!pp && bar0) { pp = document.createElement('div'); pp.id = 'c10g-cart'; pp.className = 'c10g'; bar0.insertAdjacentElement('afterend', pp); }
        if (pp) pp.innerHTML = lines.length ? bagPanel(lines, 'page', pickTopUps(list, remainingLocal, inBag, 4)) : '';
        placeLines('CartItem-', reminders);
      }
    });
  }

  // top-up "Add to bag"
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-c10g-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch(ROOT + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute('data-c10g-add'), 10), quantity: 1 }], sections: 'cart-icon-bubble' })
    }).then(function (r) { return r.json(); }).then(function (res) {
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      try {
        if (res && res.sections && res.sections['cart-icon-bubble']) {
          var bubble = document.getElementById('cart-icon-bubble');
          var html = new DOMParser().parseFromString(res.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          if (bubble && html) bubble.innerHTML = html.innerHTML;
        }
      } catch (err) {}
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.updateShipping === 'function') drawer.updateShipping();
      else window.location.href = CART_URL;
    }).catch(function () { btn.disabled = false; btn.textContent = 'Add to bag'; });
  });

  /* ---------- collection tile ---------- */

  // the bar at the bottom of the tile: how to get the gift, counting down as they add to the bag
  function tileBar(o, lines) {
    var pr = progress(o, lines || [], null);
    if (pr.done) return { cls: 'is-done', text: o.auto ? 'Unlocked · in your bag' : 'Unlocked · choose it in your bag' };
    if (o.unit === 'money') {
      return pr.have > 0
        ? { cls: 'is-part', text: money(toLocal(pr.remaining)) + ' more to unlock' }
        : { cls: 'is-start', text: 'Spend ' + money(toLocal(o.min), true) + ' · Get it free' };
    }
    var left = Math.ceil(pr.remaining);
    if (pr.have > 0) return { cls: 'is-part', text: left + ' more to unlock' };
    if (o.rule.kind === 'products' && o.rule.ids.length === 1 && o.min === 1) return { cls: 'is-start', text: 'Buy it · Get it free' };
    return { cls: 'is-start', text: 'Buy any ' + o.min + ' · Get it free' };
  }

  // same classes as the theme's "Add to bag" button, so it gets the same height, font and padding
  var BAR_CLASS = 'quick-add__submit primary-btn button button--full-width button--secondary c10g-tile-bar';
  // keep the bar's wording on one line: shrink the text a little on narrow (phone) tiles if it doesn't fit
  function fitBar(el) {
    var span = el && el.firstChild;
    if (!span || !span.style) return;
    span.style.fontSize = '';
    span.style.letterSpacing = '';
    var size = parseFloat(getComputedStyle(span).fontSize) || 12;
    var guard = 0;
    while (span.scrollWidth > el.clientWidth - 8 && size > 9 && guard++ < 12) {
      size -= 0.5;
      span.style.fontSize = size + 'px';
      span.style.letterSpacing = '0';
    }
  }
  var fitTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(function () { fitBar(document.querySelector('.c10g-tile-bar')); }, 150);
  });

  var LAST_LINES = [];
  function renderCollectionTile(lines) {
    if (lines) LAST_LINES = lines;
    var C = CFG.collection;
    if (!C || (C.page && C.page > 1)) return;
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var o = OFFERS.filter(function (x) { return x.stockChecked && !x.soldOut && tileMatches(x); })[0];
    var existing = grid.querySelector('.c10g-tile');
    if (!o) { if (existing) existing.remove(); return; }
    var bar = tileBar(o, LAST_LINES);
    if (existing) {
      var b = existing.querySelector('.c10g-tile-bar');
      if (b) { b.className = BAR_CLASS + ' ' + bar.cls; b.firstChild.textContent = bar.text; fitBar(b); }
      return;
    }
    // built from the theme's own product card classes (copied from a card already on the page),
    // so the tile lines up with its neighbours: same image size, title font and button position
    var ref = grid.querySelector('.card');
    var refInner = grid.querySelector('.card__inner');
    var cardClass = ref ? ref.className.replace(/card--text/g, 'card--media') : 'card card--standard card--media card--extend-height';
    var innerClass = refInner ? refInner.className : 'card__inner ratio';
    var li = document.createElement('li');
    li.className = 'grid__item c10g-tile';
    li.innerHTML = '<div class="card-wrapper product-card-wrapper">' +
      '<div class="' + esc(cardClass) + '" style="--ratio-percent: 100%;">' +
        '<div class="' + esc(innerClass) + '" style="--ratio-percent: 100%;">' +
          '<div class="card__media"><div class="media media--transparent">' + img(o.gifts[0].image, o.giftName, 600) + '</div></div>' +
          '<div class="c10g c10g-tile-label"><span class="c10g-pill c10g-pill-dark">' + esc((offerName(o) || 'Brand') + ' offer') + '</span></div>' +
        '</div>' +
        '<div class="card__content"><div class="card__information">' +
          '<h3 class="card__heading h5">' + esc(headline(o)) + '</h3>' +
          '<div class="card-information"><p class="c10g-tile-note">' + (o.auto ? 'Free gift added to your bag automatically' : 'Free with qualifying purchase') + '</p></div>' +
        '</div>' +
        '<div class="quick-add"><div class="' + BAR_CLASS + ' ' + bar.cls + '" aria-live="polite"><span>' + esc(bar.text) + '</span></div></div>' +
        '</div>' +
      '</div></div>';
    var items = grid.children;
    if (items.length >= 2) grid.insertBefore(li, items[2] || null); else grid.appendChild(li);
    fitBar(li.querySelector('.c10g-tile-bar'));
  }

  /* ---------- wiring ---------- */

  var timer = null;
  function refreshSoon(delay) {
    clearTimeout(timer);
    timer = setTimeout(refresh, delay == null ? 350 : delay);
  }

  function refresh() {
    return fetchCart().then(function (cart) {
      var lines = countedLines(cart);
      return addCollections(lines, OFFERS).then(function () {
        // only check stock for offers this page is about to show
        var P = CFG.product;
        var pdp = P ? { id: P.id, vendor: P.vendor, collections: P.collections } : null;
        var shown = OFFERS.filter(function (o) {
          if (o.isSpend) return o === SPEND;
          if (pdp && matches(o, pdp)) return true;
          if (tileMatches(o)) return true;
          return lines.some(function (l) { return matches(o, l); });
        });
        return checkStock(shown);
      }).then(function () {
        OFFERS = live(OFFERS);
        if (SPEND && SPEND.soldOut) SPEND = null;
        renderCollectionTile(lines);
        if (CFG.product) renderProduct(lines);
        return renderBag(lines);
      });
    }).catch(function (e) { if (window.console) console.warn('[c10-gwp]', e); });
  }

  // re-render when anything changes the bag (theme, BOGOS or us)
  function watchCartRequests() {
    var isCartWrite = function (url) { return /\/cart\/(add|change|update|clear)(\.js)?/.test(String(url || '')); };
    if (window.fetch && !window.fetch.__c10g) {
      var orig = window.fetch;
      var wrapped = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url);
        var p = orig.apply(this, arguments);
        if (isCartWrite(url)) p.then(function () { refreshSoon(); }, function () {});
        return p;
      };
      wrapped.__c10g = true;
      window.fetch = wrapped;
    }
    var open = XMLHttpRequest.prototype.open;
    if (!open.__c10g) {
      var xo = function (method, url) {
        if (isCartWrite(url)) this.addEventListener('loadend', function () { refreshSoon(); });
        return open.apply(this, arguments);
      };
      xo.__c10g = true;
      XMLHttpRequest.prototype.open = xo;
    }
  }

  // the drawer rebuilds its contents every time it opens; put our panel back when it does
  function watchContainers() {
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    if (inner) new MutationObserver(function () {
      if (!inner.querySelector('#c10g-drawer')) refreshSoon(50);
    }).observe(inner, { childList: true });

    var drawer = document.getElementById('CartDrawer');
    if (drawer) new MutationObserver(function () {
      var i2 = document.querySelector('#CartDrawer .drawer__inner');
      if (i2 && i2 !== inner) { inner = i2; refreshSoon(50); }
    }).observe(drawer, { childList: true });

    // bag page: the theme replaces the item list when quantities change
    var items = document.querySelector('#main-cart-items .js-contents');
    if (items) new MutationObserver(function () { refreshSoon(50); }).observe(items, { childList: true });

    var gridWrap = document.getElementById('ProductGridContainer');
    if (gridWrap) new MutationObserver(function () { renderCollectionTile(); }).observe(gridWrap, { childList: true });

    // product page: variant or quantity change
    if (CFG.product) {
      document.addEventListener('change', function (e) {
        if (e.target.closest && e.target.closest('[id^="MainProduct-"]')) refreshSoon(150);
      });
      document.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[id^="MainProduct-"] .quantity__button')) refreshSoon(150);
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function () { refreshSoon(150); });
      }
    }
  }

  function start() {
    waitForOffers().then(function (raw) {
      raw.forEach(function (o) {
        (o.gift_products || []).forEach(function (g) {
          (g.variants || []).forEach(function (v) { giftVariantIds[String(v.original_variant_shopify_id)] = 1; });
        });
      });
      OFFERS = raw.map(parseOffer).filter(Boolean);
      var spends = OFFERS.filter(function (o) { return o.isSpend; })
        .sort(function (a, b) { return (b.priority - a.priority) || (a.min - b.min); });
      SPEND = spends[0] || null;
      if (!OFFERS.length) return;
      return spendGiftInfo().then(function () {
        watchCartRequests();
        watchContainers();
        return refresh();
      });
    });
  }

  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 });
  else setTimeout(start, 200);
})();